import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Badge, Icon, RelativeDateTime, TagBadges } from 'corso-admin-ui';
import {
  crewClaimRollupStatusCodeName,
  CrewMerchantApi,
  isEnum,
  shippingClaimReasonName,
  StoreProductStatus,
  storeProductStatusName,
} from 'corso-types';
import { ComponentProps, Fragment } from 'react';
import NonPropagatingNavLink from '~/components/NonPropagatingNavLink';
import ProductImage from '~/components/ProductImage';
import ResolutionMethodBadge from '~/components/ResolutionMethodBadge';
import useIsDesktop from '~/hooks/useIsDesktop';
import { shippingClaimStatusName } from '~/utils/enumNameMaps';

type SearchRes = CrewMerchantApi.ResponseBody<'/:storeId/search', 'post'>;

type Data<Entity extends SearchRes['kind']> = Extract<
  SearchRes,
  { kind: Entity }
>['data'][number];

const shippingClaimResolutionMethodToBadgeVariant: Record<
  'Reorder' | 'Refund',
  ComponentProps<typeof Badge>['variant']
> = {
  Refund: 'danger',
  Reorder: 'success',
};

const productStatusToBadgeVariant = {
  [StoreProductStatus.active]: 'success',
  [StoreProductStatus.draft]: 'info',
  [StoreProductStatus.archived]: 'default',
  [StoreProductStatus.unlisted]: 'warning',
} satisfies Record<StoreProductStatus, ComponentProps<typeof Badge>['variant']>;

const defineClaimColumns = (isDesktop: boolean) => {
  const columnHelper = createColumnHelper<Data<'Return' | 'Warranty'>>();

  const columnDef = [
    columnHelper.accessor('externalId', {
      header: 'Claim',
      cell: ({ getValue, row: { original } }) => (
        <NonPropagatingNavLink
          to={`${original.id}`}
          className="flex items-center justify-between font-semibold"
        >
          #{getValue()}
          {original.lineItemHasError && (
            <Icon icon="warning" className="inline text-amber-600" />
          )}
        </NonPropagatingNavLink>
      ),
    }),
    columnHelper.accessor('createdOn', {
      header: 'Date',
      cell: ({ getValue }) => (
        <RelativeDateTime data-testid="claimDate" dateTime={getValue()} />
      ),
    }),
    columnHelper.accessor('customerName', { header: 'Customer' }),
    columnHelper.accessor('claimRollupStatusCode', {
      header: 'Status',
      cell: ({ getValue }) => crewClaimRollupStatusCodeName[getValue()],
    }),
    columnHelper.accessor('resolutionMethods', {
      header: 'Resolution Methods',
      cell: ({ getValue }) => {
        const resolutionMethods = getValue();

        return (
          <div className="flex flex-wrap gap-1">
            {resolutionMethods.map((resolutionMethod, index) => (
              <Fragment key={resolutionMethod}>
                {/* whitespace-only text is dropped by flex layout but keeps the accessible name readable */}
                {index > 0 && ' '}
                <ResolutionMethodBadge resolutionMethod={resolutionMethod} />
              </Fragment>
            ))}
          </div>
        );
      },
    }),

    columnHelper.accessor('tags', {
      header: 'Tags',
      cell: ({ getValue }) => <TagBadges tags={getValue()} />,
    }),
  ];

  const mobileColumDef = columnDef.filter(
    (column) =>
      column.header !== 'Resolution Methods' &&
      column.header !== 'Status' &&
      column.header !== 'Tags',
  );

  return isDesktop ? columnDef : mobileColumDef;
};

const defineShippingColumns = (isDesktop: boolean) => {
  const columnHelper = createColumnHelper<Data<'Shipping'>>();

  const columnDef = [
    columnHelper.accessor('id', {
      header: 'Claim',
      cell: (info) => (
        <NonPropagatingNavLink
          to={`${info.getValue()}`}
          className="flex items-center justify-between font-semibold"
        >
          #{info.getValue()}
        </NonPropagatingNavLink>
      ),
    }),
    columnHelper.accessor('createdOn', {
      header: 'Date',
      cell: ({ getValue }) => (
        <RelativeDateTime data-testid="shipmentDate" dateTime={getValue()} />
      ),
    }),
    columnHelper.accessor('customerName', { header: 'Customer' }),

    columnHelper.accessor('customerEmail', { header: 'Email' }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => shippingClaimStatusName[getValue()],
    }),
    columnHelper.accessor('resolutionMethod', {
      header: 'Resolution Method',
      cell: ({ getValue }) => {
        const resolutionMethod = getValue();

        return (
          <div className="flex flex-wrap gap-1">
            <Badge
              variant={
                shippingClaimResolutionMethodToBadgeVariant[resolutionMethod]
              }
            >
              {resolutionMethod}
            </Badge>
          </div>
        );
      },
    }),
    columnHelper.accessor('reason', {
      header: 'Reason',
      cell: ({ getValue }) => shippingClaimReasonName[getValue()],
    }),
    columnHelper.accessor('tags', {
      header: 'Tags',
      cell: ({ getValue }) => <TagBadges tags={getValue()} />,
    }),
  ];

  const mobileColumDef = columnDef.filter(
    (column) =>
      column.header !== 'Status' &&
      column.header !== 'Reason' &&
      column.header !== 'Resolution Method' &&
      column.header !== 'Email' &&
      column.header !== 'Tags',
  );

  return isDesktop ? columnDef : mobileColumDef;
};

const defineRegistrationColumns = (isDesktop: boolean) => {
  const columnHelper = createColumnHelper<Data<'Registration'>>();

  const columnDef = [
    columnHelper.accessor('externalId', {
      header: 'Registration',
      cell: ({ getValue, row: { original } }) => (
        <NonPropagatingNavLink
          to={`${original.id}`}
          className="flex items-center justify-between font-semibold"
        >
          #{getValue()}
        </NonPropagatingNavLink>
      ),
    }),

    columnHelper.accessor('createdOn', {
      header: 'Date',
      cell: ({ getValue }) => (
        <RelativeDateTime
          data-testid="registrationDate"
          dateTime={getValue()}
        />
      ),
    }),

    columnHelper.accessor('customerName', { header: 'Customer' }),

    columnHelper.accessor('customerEmail', { header: 'Email' }),

    columnHelper.accessor('registrationChannelName', {
      header: 'Channel',
      cell: ({ getValue }) => {
        getValue();

        return (
          <div className="flex flex-wrap gap-1">
            <Badge variant="default">{getValue()}</Badge>
          </div>
        );
      },
    }),

    columnHelper.accessor('canceledOn', {
      header: 'Status',
      cell: ({ getValue }) => {
        const canceledOn = getValue();

        return canceledOn ? (
          <Badge variant="danger">Canceled</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        );
      },
    }),

    columnHelper.accessor('tags', {
      header: 'Tags',
      cell: ({ getValue }) => <TagBadges tags={getValue()} />,
    }),
  ];

  const mobileColumDef = columnDef.filter(
    (column) =>
      column.header !== 'Email' &&
      column.header !== 'Channel' &&
      column.header !== 'Tags' &&
      column.header !== 'Status',
  );

  return isDesktop ? columnDef : mobileColumDef;
};

const defineOrderColumns = () => {
  const columnHelper = createColumnHelper<Data<'Order'>>();

  const columnDef = [
    columnHelper.accessor('orderNo', {
      header: 'Order Number',
      cell: ({ getValue }) => (
        <div className="flex items-center justify-between font-semibold">
          #{getValue()}
        </div>
      ),
    }),

    columnHelper.accessor('createdOn', {
      header: 'Date',
      cell: ({ getValue }) => (
        <RelativeDateTime data-testid="orderDate" dateTime={getValue()} />
      ),
    }),

    columnHelper.accessor('customerName', { header: 'Customer' }),

    columnHelper.accessor('customerEmail', { header: 'Email' }),
  ];

  return columnDef;
};

const defineProductColumns = (isDesktop: boolean) => {
  const columnHelper = createColumnHelper<Data<'Product'>>();

  const columnDef = [
    columnHelper.accessor('title', {
      header: 'Product',
      cell: ({ getValue, row: { original } }) => (
        <NonPropagatingNavLink
          to={`${original.id}`}
          className="flex items-center gap-2 font-semibold"
        >
          <ProductImage
            src={original.imgUrl}
            alt={getValue()}
            className="size-8 shrink-0"
          />
          {getValue()}
        </NonPropagatingNavLink>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        if (!status) return null;

        // the column is free-form, so CSV uploads can carry values outside the enum
        return isEnum(status, StoreProductStatus) ? (
          <Badge variant={productStatusToBadgeVariant[status]}>
            {storeProductStatusName[status]}
          </Badge>
        ) : (
          <Badge>{status}</Badge>
        );
      },
    }),
    columnHelper.accessor('productType', { header: 'Category' }),
    columnHelper.accessor('isOffPlatform', {
      header: 'Source',
      cell: ({ getValue }) =>
        getValue() ? (
          <Badge>Corso</Badge>
        ) : (
          <Badge variant="success">Shopify</Badge>
        ),
    }),
  ];

  const mobileColumDef = columnDef.filter(
    (column) => column.header !== 'Category',
  );

  return isDesktop ? columnDef : mobileColumDef;
};

type ColumnDefinitions = {
  [K in SearchRes['kind']]: (isDesktop: boolean) => {
    [P in keyof Data<K>]: ColumnDef<Data<K>, Data<K>[P]>;
  }[keyof Data<K>][];
};

const columnDefinitions: ColumnDefinitions = {
  Return: defineClaimColumns,
  Warranty: defineClaimColumns,
  Shipping: defineShippingColumns,
  Registration: defineRegistrationColumns,
  Order: defineOrderColumns,
  Product: defineProductColumns,
};

export const useColumnDefinition = <Entity extends SearchRes['kind']>(
  entity: Entity,
) => {
  const isDesktop = useIsDesktop();

  return columnDefinitions[entity](isDesktop);
};
